/**
 * Values shared between the Bevakningar page and the search page's save
 * button. They live here rather than in SavedSearchesPage.js so that importing
 * one constant does not pull the whole page — and its Modal, its CSS and its
 * duck — into the search page's bundle.
 */
import { parseOriginParam, parseRadiusKm } from '../../util/geoAreaCore';

/**
 * Query param that puts the search page into "editing this bevakning" mode.
 * The save button reads it to decide between creating a new saved search and
 * updating the one whose id it carries.
 */
export const EDIT_PARAM = 'editBevakning';

/**
 * Marker added to the return URL when a logged-out user presses save. On the
 * way back from login the button sees it and completes the save the user
 * already asked for, rather than making them press the button again (BR-4).
 */
export const SAVE_MARKER = 'saveSearch';

/**
 * Query params that describe how results are shown, not what is being looked
 * for. A search made only of these is not something anyone can watch.
 *
 * KEEP IN SYNC with `VOLATILE_KEYS` in server/api-util/savedSearchCriteria.js.
 * The server refuses to save a search whose meaningful params are empty, so if
 * these two lists drift apart the button will offer a save that the server then
 * rejects — which is exactly the dead end this list exists to prevent.
 */
const NON_CRITERIA_PARAMS = [
  'page',
  'sort',
  'mapSearch',
  'origin',
  // A distance chip counts only with the area it is drawn around — see
  // hasSearchCriteria (and distanceCriterion on the server, P25009-425).
  'radius',
  'bounds',
  SAVE_MARKER,
  EDIT_PARAM,
];

/**
 * Is there anything in this query string worth watching?
 *
 * A bevakning on an unfiltered search would match every listing published from
 * now on, so the server rejects it. The save button asks this first and hides
 * itself when the answer is no — offering to save nothing, and worse sending a
 * logged-out visitor through login to be told "Kunde inte spara", is a dead end
 * rather than a feature.
 *
 * @param {String} search the search page's query string
 * @returns {Boolean}
 */
export const hasSearchCriteria = search => {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));

  /* "Inom 50 km" around the buyer's area is something to watch on its own: a
   * local buyer wanting anything near (P25009-425). */
  const watchesDistance = !!(
    parseRadiusKm(params.get('radius')) && parseOriginParam(params.get('origin'))
  );

  return (
    watchesDistance ||
    Array.from(params.keys()).some(key => !NON_CRITERIA_PARAMS.includes(key) && !!params.get(key))
  );
};
